import type { InfiniteData } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationsKeys } from "web_myplann/Api/Notifications/notificatioKeys";
import {
	type NotificationListResponseDTO,
	patchNotificationsIdReadV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

export const useNotificationMarksAsReadMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse((notificationId: number) =>
			patchNotificationsIdReadV2(notificationId),
		),
		onMutate: async (notificationId: number) => {
			await Promise.all([
				queryClient.cancelQueries({
					queryKey: NotificationsKeys.infiniteGroupedList(true),
				}),
				queryClient.cancelQueries({
					queryKey: NotificationsKeys.infiniteGroupedList(false),
				}),
				queryClient.cancelQueries({ queryKey: NotificationsKeys.count() }),
			]);

			const keyUnread = NotificationsKeys.infiniteGroupedList(true);
			const keyAll = NotificationsKeys.infiniteGroupedList(false);

			const previousUnread =
				queryClient.getQueryData<
					InfiniteData<NotificationListResponseDTO, number>
				>(keyUnread);
			const previousAll =
				queryClient.getQueryData<
					InfiniteData<NotificationListResponseDTO, number>
				>(keyAll);

			const applyReadAtToUnread = (
				data?: InfiniteData<NotificationListResponseDTO, number>,
			) => {
				if (!data) return;
				const pages = data.pages.map((page, idx) => ({
					...page,
					data: page.data.filter((n) => n.id !== notificationId),
					meta:
						idx === 0 && typeof page.meta?.unreadCount === "number"
							? {
									...page.meta,
									unreadCount: Math.max(0, page.meta.unreadCount - 1),
								}
							: page.meta,
				}));
				return { ...data, pages } as InfiniteData<
					NotificationListResponseDTO,
					number
				>;
			};

			const applyReadAtToAll = (
				data?: InfiniteData<NotificationListResponseDTO, number>,
			) => {
				if (!data) return;
				const pages = data.pages.map((page) => ({
					...page,
					data: page.data.map((n) =>
						n.id === notificationId
							? { ...n, readAt: n.readAt ?? new Date().toISOString() }
							: n,
					),
				}));
				return { ...data, pages } as InfiniteData<
					NotificationListResponseDTO,
					number
				>;
			};

			if (previousUnread)
				queryClient.setQueryData(
					keyUnread,
					applyReadAtToUnread(previousUnread),
				);
			if (previousAll)
				queryClient.setQueryData(keyAll, applyReadAtToAll(previousAll));

			return { previousUnread, previousAll };
		},
		onError: (_err, _id, context) => {
			if (context?.previousUnread) {
				queryClient.setQueryData(
					NotificationsKeys.infiniteGroupedList(true),
					context.previousUnread,
				);
			}
			if (context?.previousAll) {
				queryClient.setQueryData(
					NotificationsKeys.infiniteGroupedList(false),
					context.previousAll,
				);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.infiniteGroupedList(true),
			});
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.infiniteGroupedList(false),
			});
			queryClient.invalidateQueries({ queryKey: NotificationsKeys.count() });
		},
	});
};
